import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Link } from 'react-router-dom';
import { getReportTypeLabel, getStatusColor, formatDate, getTotalVotes } from '../utils/labelHelpers';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import '../styles/ActionReports.css';

const DISTRICT_ROLES = ['dCOM', 'dCOM Chair', 'dCOM Member', 'DS'];

const ActionReports = () => {
  const toast = useToast();
  const { currentUser, districts } = useAuth();
  const [actionReports, setActionReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [filteredReports, setFilteredReports] = useState([]);
  const [editingReport, setEditingReport] = useState(null);

  const isDCOM = DISTRICT_ROLES.includes(currentUser?.role);
  const userDistrictIds = currentUser?.districtIds || (currentUser?.districtId ? [currentUser.districtId] : []);
  const userDistrictNames = isDCOM && districts.length > 0
    ? userDistrictIds.map(id => districts.find(d => d.id === id)?.name).filter(Boolean)
    : [];

  const filterReports = useCallback(() => {
    let filtered = actionReports;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(report =>
        report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.candidateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.committee?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(report => report.status === statusFilter);
    }

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(report => report.type === typeFilter);
    }

    setFilteredReports(filtered);
  }, [actionReports, searchTerm, statusFilter, typeFilter]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterReports();
  }, [filterReports]);

  const fetchData = async () => {
    try {
      const [reportsSnapshot, candidatesSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'actionReports'), orderBy('date', 'desc'))),
        getDocs(collection(db, 'candidates')),
      ]);

      const candidatesList = candidatesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      let reportsList = reportsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // District-scoped users see only reports for candidates in their district(s)
      if (isDCOM && userDistrictNames.length > 0) {
        const districtCandidateIds = new Set(
          candidatesList
            .filter(c => userDistrictNames.includes(c.district))
            .map(c => c.id)
        );
        reportsList = reportsList.filter(r => districtCandidateIds.has(r.candidateId));
      }

      setActionReports(reportsList);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleEditReport = (report) => {
    setEditingReport({
      ...report,
      date: report.date ? new Date(report.date).toISOString().split('T')[0] : ''
    });
  };

  const handleUpdateReport = async () => {
    if (!editingReport.title || !editingReport.date) {
      toast.warning('Please fill in required fields (Title and Date)');
      return;
    }

    try {
      const reportRef = doc(db, 'actionReports', editingReport.id);
      const updateData = {
        ...editingReport,
        updatedAt: new Date()
      };

      // Remove the id from the update data
      delete updateData.id;

      await updateDoc(reportRef, updateData);
      await fetchData(); // Refresh the data
      setEditingReport(null);
      toast.success('Action report updated.');
    } catch (error) {
      console.error('Error updating action report:', error);
      toast.error('Error updating action report. Please try again.');
    }
  };

  const getStatusStats = () => {
    const stats = {
      pending: 0,
      approved: 0,
      denied: 0,
      deferred: 0,
      completed: 0
    };

    actionReports.forEach(report => {
      if (stats.hasOwnProperty(report.status)) {
        stats[report.status]++;
      }
    });

    return stats;
  };

  const exportToCSV = () => {
    if (filteredReports.length === 0) {
      toast.warning('No data to export.');
      return;
    }

    const headers = [
      'Title',
      'Candidate Name',
      'Type',
      'Status',
      'Date',
      'Committee',
      'Recommendation',
      'Vote Yes',
      'Vote No',
      'Vote Abstain',
      'Total Votes',
      'Interview Team',
      'Notes',
      'Next Steps'
    ];

    const csvContent = [
      headers.join(','),
      ...filteredReports.map(report => [
        `"${report.title || ''}"`,
        `"${report.candidateName || ''}"`,
        `"${getReportTypeLabel(report.type) || ''}"`,
        `"${report.status?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || ''}"`,
        `"${formatDate(report.date)}"`,
        `"${report.committee || ''}"`,
        `"${report.recommendation?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || ''}"`,
        `"${report.vote?.yes || 0}"`,
        `"${report.vote?.no || 0}"`,
        `"${report.vote?.abstain || 0}"`,
        `"${getTotalVotes(report.vote)}"`,
        `"${report.interviewTeam || ''}"`,
        `"${(report.notes || '').replace(/"/g, '""')}"`,
        `"${(report.nextSteps || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    downloadFile(csvContent, 'action-reports.csv', 'text/csv');
  };

  const exportToJSON = () => {
    if (filteredReports.length === 0) {
      toast.warning('No data to export.');
      return;
    }

    const exportData = {
      exportDate: new Date().toISOString(),
      totalReports: filteredReports.length,
      filters: {
        searchTerm,
        statusFilter,
        typeFilter
      },
      reports: filteredReports.map(report => ({
        id: report.id,
        title: report.title,
        candidateName: report.candidateName,
        candidateId: report.candidateId,
        type: report.type,
        typeLabel: getReportTypeLabel(report.type),
        status: report.status,
        date: report.date,
        committee: report.committee,
        recommendation: report.recommendation,
        vote: report.vote,
        totalVotes: getTotalVotes(report.vote),
        interviewTeam: report.interviewTeam,
        notes: report.notes,
        nextSteps: report.nextSteps,
        createdAt: report.createdAt,
        updatedAt: report.updatedAt
      }))
    };

    const jsonContent = JSON.stringify(exportData, null, 2);
    downloadFile(jsonContent, 'action-reports.json', 'application/json');
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="action-reports-container">
        <div className="loading-spinner">Loading action reports...</div>
      </div>
    );
  }

  const statusStats = getStatusStats();

  return (
    <div className="action-reports-container">
      <div className="page-header">
        <h1>Action Reports & Votes</h1>
        <p>Track committee decisions and voting records</p>
      </div>

      <div className="stats-overview">
        <div className="stat-card">
          <h3>Total Reports</h3>
          <div className="stat-number">{actionReports.length}</div>
        </div>
        <div className="stat-card pending">
          <h3>Pending</h3>
          <div className="stat-number">{statusStats.pending}</div>
        </div>
        <div className="stat-card approved">
          <h3>Approved</h3>
          <div className="stat-number">{statusStats.approved}</div>
        </div>
        <div className="stat-card denied">
          <h3>Denied</h3>
          <div className="stat-number">{statusStats.denied}</div>
        </div>
        <div className="stat-card deferred">
          <h3>Deferred</h3>
          <div className="stat-number">{statusStats.deferred}</div>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search reports, candidates, or committees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-controls">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="deferred">Deferred</option>
            <option value="completed">Completed</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="dcom-review">DCOM Review</option>
            <option value="bom-review">BOM Review</option>
            <option value="candidacy-interview">Candidacy Interview</option>
            <option value="provisional-review">Provisional Review</option>
            <option value="ordination-review">Ordination Review</option>
            <option value="annual-review">Annual Review</option>
            <option value="other">Other</option>
          </select>

          <div className="export-controls">
            <button 
              className="export-btn"
              onClick={() => exportToCSV()}
              title="Export filtered results to CSV"
            >
              Export CSV
            </button>
            <button 
              className="export-btn"
              onClick={() => exportToJSON()}
              title="Export filtered results to JSON"
            >
              Export JSON
            </button>
          </div>
        </div>
      </div>

      <div className="reports-grid">
        {filteredReports.length === 0 ? (
          <div className="no-reports">
            <p>No action reports found matching your criteria.</p>
          </div>
        ) : (
          filteredReports.map(report => (
            <div key={report.id} className="report-card">
              <div className="report-header">
                <div className="report-title">
                  <h3>{report.title}</h3>
                  <div className="report-meta">
                    <span className="report-type">{getReportTypeLabel(report.type)}</span>
                    <span className={`report-status ${getStatusColor(report.status)}`}>
                      {report.status?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                </div>
              </div>

              <div className="report-details">
                <div className="detail-item">
                  <label>Candidate:</label>
                  <span>{report.candidateName}</span>
                </div>
                <div className="detail-item">
                  <label>Date:</label>
                  <span>{formatDate(report.date)}</span>
                </div>
                <div className="detail-item">
                  <label>Committee:</label>
                  <span>{report.committee || 'Not specified'}</span>
                </div>
                {report.recommendation && (
                  <div className="detail-item">
                    <label>Recommendation:</label>
                    <span className={`recommendation ${report.recommendation}`}>
                      {report.recommendation.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                )}
                {getTotalVotes(report.vote) > 0 && (
                  <div className="vote-summary">
                    <label>Vote:</label>
                    <div className="vote-display">
                      <span className="vote-yes">Yes: {report.vote?.yes || 0}</span>
                      <span className="vote-no">No: {report.vote?.no || 0}</span>
                      <span className="vote-abstain">Abstain: {report.vote?.abstain || 0}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="report-actions">
                <Link 
                  to={`/candidates/${report.candidateId}`} 
                  className="view-candidate-btn"
                >
                  View Candidate
                </Link>
                <button 
                  className="edit-report-btn"
                  onClick={() => handleEditReport(report)}
                >
                  Edit Report
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {editingReport && (
        <QuickEditModal 
          report={editingReport}
          setReport={setEditingReport}
          onSave={handleUpdateReport}
          onCancel={() => setEditingReport(null)}
        />
      )}

      <div className="reports-summary">
        <p>Showing {filteredReports.length} of {actionReports.length} action reports</p>
      </div>
    </div>
  );
};

const QuickEditModal = ({ report, setReport, onSave, onCancel }) => {
  return (
    <div className="add-report-modal">
      <div className="modal-content">
        <h3>Quick Edit Action Report</h3>
        <div className="report-form">
          <div className="form-row">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={report.title || ''}
                onChange={(e) => setReport({...report, title: e.target.value})}
                placeholder="e.g., DCOM Annual Review 2024"
                required
              />
            </div>
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                value={report.date || ''}
                onChange={(e) => setReport({...report, date: e.target.value})}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Interview team</label>
            <input
              type="text"
              value={report.interviewTeam || ''}
              onChange={(e) => setReport({...report, interviewTeam: e.target.value})}
              placeholder="e.g., Rev. Smith, Rev. Johnson, Dr. Williams"
            />
          </div>

          <div className="vote-section">
            <label>Vote Count</label>
            <div className="vote-inputs">
              <div className="vote-input">
                <label>Yes</label>
                <input
                  type="number"
                  min="0"
                  value={report.vote?.yes || 0}
                  onChange={(e) => setReport({
                    ...report, 
                    vote: {...(report.vote || {}), yes: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
              <div className="vote-input">
                <label>No</label>
                <input
                  type="number"
                  min="0"
                  value={report.vote?.no || 0}
                  onChange={(e) => setReport({
                    ...report, 
                    vote: {...(report.vote || {}), no: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
              <div className="vote-input">
                <label>Abstain</label>
                <input
                  type="number"
                  min="0"
                  value={report.vote?.abstain || 0}
                  onChange={(e) => setReport({
                    ...report, 
                    vote: {...(report.vote || {}), abstain: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={report.notes || ''}
              onChange={(e) => setReport({...report, notes: e.target.value})}
              rows="4"
              placeholder="Meeting notes, discussion points, concerns..."
            />
          </div>
          
          <div className="form-group">
            <label>Next Steps</label>
            <textarea
              value={report.nextSteps || ''}
              onChange={(e) => setReport({...report, nextSteps: e.target.value})}
              rows="3"
              placeholder="Required actions, follow-up items..."
            />
          </div>
          
          <div className="form-group">
            <label>Status</label>
            <select
              value={report.status || 'pending'}
              onChange={(e) => setReport({...report, status: e.target.value})}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
              <option value="deferred">Deferred</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <div className="modal-actions">
          <button onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
          <button onClick={onSave} className="save-btn">
            Update Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionReports;
